import React, { useCallback, useMemo, useState } from 'react';
import {
  DATE_RANGES,
  DATE_RANGE_STORAGE_KEY,
  DashboardFiltersContext,
  type DashboardFiltersValue,
  type DateRangeKey,
  type DateRangeOption,
} from './DashboardFiltersContext';

function readStoredRange(): DateRangeKey {
  try {
    const stored = window.localStorage.getItem(DATE_RANGE_STORAGE_KEY);
    if (stored && DATE_RANGES.some((option) => option.key === stored)) {
      return stored as DateRangeKey;
    }
  } catch {
    // Private browsing and blocked site data both throw here; the default is fine.
  }
  return '30d';
}

function computeBounds(range: DateRangeOption): { from: string | null; to: string | null } {
  if (range.key === 'all') return { from: null, to: null };

  const now = new Date();
  const to = now.toISOString();

  if (range.key === 'ytd') {
    return { from: new Date(Date.UTC(now.getUTCFullYear(), 0, 1)).toISOString(), to };
  }

  const from = new Date(now.getTime() - (range.days ?? 30) * 24 * 60 * 60 * 1000);
  return { from: from.toISOString(), to };
}

export const DashboardFiltersProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [rangeKey, setRangeKey] = useState<DateRangeKey>(readStoredRange);

  const setRange = useCallback((key: DateRangeKey) => {
    setRangeKey(key);
    try {
      window.localStorage.setItem(DATE_RANGE_STORAGE_KEY, key);
    } catch {
      // Persisting the choice is a convenience, never a requirement.
    }
  }, []);

  const value = useMemo<DashboardFiltersValue>(() => {
    const range = DATE_RANGES.find((option) => option.key === rangeKey) ?? DATE_RANGES[1];
    return { range, setRange, bounds: computeBounds(range) };
  }, [rangeKey, setRange]);

  return <DashboardFiltersContext.Provider value={value}>{children}</DashboardFiltersContext.Provider>;
};

export default DashboardFiltersProvider;
